import { RefObject, useCallback, useEffect, useRef } from 'react';
import { useRouter } from 'next/router';
import { Category, createEventLogUnified, UIAction } from '@/common/services/logging';
import { Props as EventProps } from '@kiwicom/nitro/lib/records/Event';

import { log } from '@kiwicom/nitro/lib/services/log/logger';

export type TrackingProps = {
  category: Category;
  props: EventProps;
};

/**
 * This hook intercepts click events on a DOM element, and in case
 * the click event's target was an `a` tag with `data-smartfaqid=[id]`,
 * prevents the redirection and opens the SmartFAQ sidebar instead.
 *
 * @param trackingProps - used to send a correct log event when a SmartFAQ
 *   link is clicked on.
 *
 * @returns a RefObject. The caller can use the ref to specify on which
 * DOM element this hook should listen for click events. If the ref is
 * not used, this hook has no effect.
 */
const useOpenHelpArticle = <T extends HTMLElement>(trackingProps: TrackingProps): RefObject<T> => {
  const ref = useRef<T>(null);
  const { query, replace } = useRouter();

  const openHelpArticle = useCallback(
    (event): void => {
      const smartFaqId = event.target.dataset?.smartfaqid as string | undefined;

      if (event.target.tagName !== 'A' || !smartFaqId) return;

      event.preventDefault();

      void log(createEventLogUnified(trackingProps.category, UIAction.ButtonClicked, 'smart faq'), {
        smartFaqId: Number(smartFaqId),
        ...trackingProps.props,
      });

      void replace(
        {
          query: { ...query, help: `/faq/search/article/${smartFaqId}` },
        },
        undefined,
        { scroll: false },
      );
    },
    [query, replace, trackingProps.category, trackingProps.props],
  );

  useEffect(() => {
    const node = ref.current;

    node?.addEventListener('click', openHelpArticle);

    return () => {
      node?.removeEventListener('click', openHelpArticle);
    };
  }, [openHelpArticle]);

  return ref;
};

export default useOpenHelpArticle;
